import { useCallback, useEffect } from 'react'
import { VisitCondition } from '~/components/fusuma/sd/visitCondition'
import type { ReserveCondition } from '~/components/fusuma/sd/visitCondition'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import type { ReserveStateDatetime } from '~/features/reserve/state'
import {
  useBack,
  useReserveNavigate,
  useReserveNavigationState,
  useReserveOverlay,
} from '~/navigation'
import type { ReserveInputContext } from '~/navigation'
import { useVisitDate, useVisitTime, useVisitorsCount } from '../store/stateMachine/context'
import { useChangeReserveCondition } from '../store/totalCount'

export function TotalCountFusuma() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_TOTAL_COUNT')
  const { state, onClickTime } = useTotalCountFusuma(context)
  if (state === undefined) {
    return null
  }
  return (
    <StackableFusuma title="総人数の変更" willBeUnmounted={willBeUnmounted}>
      <VisitCondition state={state} onClickTime={onClickTime} />
    </StackableFusuma>
  )
}

export function TotalCountModal() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_TOTAL_COUNT')
  const { state, onClickTime } = useTotalCountFusuma(context)
  if (state === undefined) {
    return null
  }
  return (
    <StackableSlideUpModal
      type="modal"
      animationType="fadeIn"
      title="総人数の変更"
      willBeUnmounted={willBeUnmounted}
    >
      <VisitCondition state={state} onClickTime={onClickTime} />
    </StackableSlideUpModal>
  )
}

function useTotalCountFusuma(context?: ReserveInputContext) {
  const visitorsCount = useVisitorsCount()
  const visitDate = useVisitDate()
  const visitTime = useVisitTime()
  const change = useChangeReserveCondition()
  const back = useBack()
  const onClickTime = useCallback(
    (condition: ReserveCondition) => {
      // 予約入力とナビゲーションの状態に矛盾が生じないように、
      // 予約入力の状態遷移の完了を確認してからナビゲーションの状態遷移を実行する
      if (!change(condition)) {
        return
      }
      back('NEW_RESERVE_INPUT')
    },
    [change, back]
  )
  // onClickTime の実行によって、ナビゲーションの状態が NEW_RESERVE_INPUT に遷移したら、
  // 遷移先でナビゲーションイベントを発火して ReserveNavigationContext の人数日時を更新する
  const state = useReserveNavigationState('NEW_RESERVE_INPUT')
  const navigate = useReserveNavigate()
  useEffect(() => {
    if (
      state === undefined ||
      visitorsCount === 0 ||
      visitDate === undefined ||
      visitTime === undefined
    ) {
      return
    }
    // state が NEW_RESERVE_INPUT に遷移し、人数日時が変更されていたら、
    // 予約入力の context から取得した人数日時で ナビゲーションの context の人数日時を更新するイベントを発火する
    // これにより、総人数変更ふすまで変更された人数日時をナビゲーションの人数日時に反映する
    if (
      state.context.visitorsCount !== visitorsCount ||
      state.context.visitDate !== visitDate ||
      state.context.visitTime !== visitTime
    ) {
      navigate({
        type: 'changeReserveCondition',
        visitorsCount,
        visitDate,
        visitTime,
      })
    }
  }, [visitorsCount, visitDate, visitTime, state, navigate])

  // hooks の呼び出し順の制限により、context が undefined の場合も、この hook を呼び出す必要がある
  // context が undefined の場合は、返り値の state に undefined を設定し、
  // 呼び出し側では state が undefined かどうかを確認してふすまの表示を制御する
  if (context === undefined || visitorsCount === 0 || visitDate === undefined) {
    return { state: undefined, onClickTime: () => {} }
  }

  // 総人数変更ふすまで人数日時を変更し、ブラウザフォワードで再度総人数変更ふすまを開いたとき、
  // 変更後の人数日時が表示されるように、visitorsCount と visitDate は 予約入力の context から取得している
  // restrantId と planId は更新されることはないので、ナビゲーション の context から取得している
  const dateTimeState: ReserveStateDatetime = {
    type: 'datetime',
    restaurantId: context.restaurantId,
    planId: context.planId,
    visitDate,
    visitorsCount,
  }
  return { state: dateTimeState, onClickTime }
}
